/**
 * Surveys Service
 */
import { FileMetadata } from "@/hooks/use-file-upload.ts"

// Cached API base URL loaded from aws-exports.json
let SURVEYS_API_URL = ""

/**
 * Load the surveys API URL from aws-exports.json configuration.
 *
 * Falls back to a relative /api/surveys path for local development
 * when no explicit API URL is configured.
 *
 * @returns The full URL for the surveys API endpoint.
 */
async function loadApiUrl(): Promise<string> {
  if (SURVEYS_API_URL) {
    return SURVEYS_API_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    // VITE_API_URL env var takes precedence (used with npm run dev --host).
    // Falls back to apiUrl from aws-exports.json (CI-generated for deployed envs).
    // Last resort: relative path (will 404 locally but works behind CloudFront).
    const baseUrl = import.meta.env.VITE_API_URL || config.apiUrl || ""
    SURVEYS_API_URL = baseUrl ? `${baseUrl}/api/surveys` : "/api/surveys"
    return SURVEYS_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Surveys API URL not configured")
  }
}


export interface Survey {
  mapSurveyId: string
  countryName: string
  countryIso3: string
  year: string
  surveyType: string
  surveyStatus: string
  hasGps: boolean
  hasMicro: false
  reportBiblioId: string
  reportBiblioFile: string
  feverTreatmentSeeking: DataTopicStatus
  antimalarials: DataTopicStatus
  chemoprevention: DataTopicStatus
  itn: DataTopicStatus
  irs: DataTopicStatus
  improvedHousing: DataTopicStatus
  rdt: DataTopicStatus
  microscopy: DataTopicStatus
  dataTopics?: DataTopic[]
  notes?: SurveyNote[]
  dhsSurveyId?: string
  doi?: string
  fieldworkStart?: string
  fieldworkEnd?: string
  implementingOrganisation?: string
  phase?: string
  title?: string
  surveyYearLabel?: string
  populationRepresentativeness?: "National" | "Subnational" | "School"
}

export interface SurveyNote {
  mapSurveyId: string
  note: string
  createdBy: string
  createdOn: string
}

export interface NewSurveyRequest {
  mapSurveyId: string
  countryName: string
  countryIso3: string
  year: number
  surveyType: string
  /**
   * The targeted population scope of the survey. Required; the create-survey
   * form always supplies a value (defaulting to "National").
   */
  populationRepresentativeness: "National" | "Subnational" | "School"
  createdBy: string
  biblioIds?: string[]
  dhsSurveyId?: string
  reportBiblioId?: string
  reportBiblioFile?: string
  surveyStatus?: string
  reportFiles?: (File | FileMetadata)[]
}

export enum DataTopicStatus {
  MAY_CONTAIN = "May contain",
  DOES_NOT_CONTAIN = "Does not contain",
  DOES_CONTAIN = "Does contain",
  MICRO_DATA_EXTRACTED = "Micro Data Extracted",
  REPORT_DATA_EXTRACTED = "Report Data Extracted"
}

/** Human-readable labels for data topic field names. */
export const DATA_TOPIC_LABELS: Record<string, string> = {
  feverTreatmentSeeking: "Fever Treatment Seeking",
  antimalarials: "Antimalarials",
  chemoprevention: "Chemoprevention",
  itn: "ITN",
  irs: "IRS",
  improvedHousing: "Housing",
  rdt: "RDT",
  microscopy: "Microscopy",
}

function dataTopicIsExtracted(status: DataTopicStatus) {
  return status === DataTopicStatus.MICRO_DATA_EXTRACTED || status === DataTopicStatus.REPORT_DATA_EXTRACTED
}

export interface DataTopic {
  name: string
  status: DataTopicStatus
}

export interface SurveyType {
  /** The short code identifying the survey type (e.g. "DHS", "MIS"). */
  surveyType: string
  /** A human-readable description of the survey type. */
  description: string
}

export interface AddSurveyNoteRequest {
  /** The MAP survey identifier (e.g. "KE2024DHS"). */
  mapSurveyId: string
  note: string
  createdBy: string
}

export interface AddReportToSurveyRequest {
  /** The MAP survey identifier (e.g. "SEN2018DHS"). */
  mapSurveyId: string
  /** The biblio sourceId as a string (e.g. "1001461050"). */
  sourceId: string
  /** The Google Drive file ID (used to construct the file link). */
  sourceFileId?: string
  /** The survey type (e.g. "DHS", "MIS") to write to biblio-sources. */
  surveyType: string
}

export interface EditDataTopicsRequest {
  /** The MAP survey identifier (e.g. "KE2024DHS"). */
  mapSurveyId: string
  feverTreatmentSeeking?: DataTopicStatus
  antimalarials?: DataTopicStatus
  chemoprevention?: DataTopicStatus
  itn?: DataTopicStatus
  irs?: DataTopicStatus
  improvedHousing?: DataTopicStatus
  rdt?: DataTopicStatus
  microscopy?: DataTopicStatus
}

/**
 * Fetch the surveys list from the GET /api/surveys endpoint.
 * The request is routed through CloudFront to the API Gateway.
 *
 * @param idToken - Auth0 access token for API authentication
 * @returns Array of Survey objects
 * @throws Error if the API call fails or returns a non-OK status
 */
export async function getSurveysList(idToken: string): Promise<Array<Survey>> {
  const apiUrl = await loadApiUrl()
  const response = await fetch(apiUrl, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const surveys: Array<Survey> = await response.json()
  surveys.forEach(survey => {
    survey.dataTopics = getExtractedDataTopicsForSurvey(survey)
  })

  return surveys
}

/**
 * Fetch the available survey types from the GET /api/surveys/types endpoint.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @returns Array of SurveyType objects containing surveyType and description.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function getSurveyTypes(idToken: string): Promise<Array<SurveyType>> {
  const apiUrl = await loadApiUrl()
  const response = await fetch(`${apiUrl}/types`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const surveyTypes: Array<SurveyType> = await response.json()
  return surveyTypes
}

export interface NewSurveyTypeRequest {
  /** The short code identifying the survey type (e.g. "DHS", "MIS"). */
  surveyType: string
  /** An optional human-readable description of the survey type. */
  description?: string
}

/**
 * Add a new survey type via POST /api/surveys/types endpoint.
 *
 * Inserts a new row into the malaria-survey-types table and returns the
 * updated full list of survey types.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param request - The new survey type payload containing surveyType and optional description.
 * @returns Updated array of SurveyType objects after the insertion.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function addSurveyType(
  idToken: string,
  request: NewSurveyTypeRequest
): Promise<Array<SurveyType>> {
  const apiUrl = await loadApiUrl()
  const response = await fetch(`${apiUrl}/types`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const surveyTypes: Array<SurveyType> = await response.json()
  return surveyTypes
}


function getExtractedDataTopicsForSurvey(survey: Survey) {
  const dataTopics: DataTopic[] = []
  if (dataTopicIsExtracted(survey.feverTreatmentSeeking)) {
    dataTopics.push({
      name: "feverTreatmentSeeking",
      status: survey.feverTreatmentSeeking,
    })
  }
  if (dataTopicIsExtracted(survey.antimalarials)) {
    dataTopics.push({
      name: "antimalarials",
      status: survey.antimalarials,
    })
  }
  if (dataTopicIsExtracted(survey.chemoprevention)) {
    dataTopics.push({
      name: "chemoprevention",
      status: survey.chemoprevention,
    })
  }
  if (dataTopicIsExtracted(survey.itn)) {
    dataTopics.push({
      name: "itn",
      status: survey.itn,
    })
  }
  if (dataTopicIsExtracted(survey.irs)) {
    dataTopics.push({
      name: "irs",
      status: survey.irs,
    })
  }
  if (dataTopicIsExtracted(survey.improvedHousing)) {
    dataTopics.push({
      name: "improvedHousing",
      status: survey.improvedHousing,
    })
  }
  if (dataTopicIsExtracted(survey.rdt)) {
    dataTopics.push({
      name: "rdt",
      status: survey.rdt,
    })
  }
  if (dataTopicIsExtracted(survey.microscopy)) {
    dataTopics.push({
      name: "microscopy",
      status: survey.microscopy,
    })
  }
  return dataTopics
}

/**
 * Add a new survey to the list via the POST /api/surveys endpoint.
 * The request is routed through CloudFront to the API Gateway.
 *
 * @param idToken - Auth0 access token for API authentication
 * @param newSurveyRequest - The new survey payload.
 * @returns The mapSurveyId of the newly created survey.
 * @throws Error if the API call fails or returns a non-OK status
 */
export async function addSurvey(idToken: string, newSurveyRequest: NewSurveyRequest): Promise<string> {
  const apiUrl = await loadApiUrl()
  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(newSurveyRequest),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const newSurveyResponse: { mapSurveyId: string } = await response.json()

  return newSurveyResponse.mapSurveyId
}

/**
 * Add/edit the user-set data topic values and get updated surveys list from POST /api/surveys/topics-user-edits endpoint.
 * The request is routed through CloudFront to the API Gateway.
 *
 * @param idToken - Auth0 access token for API authentication
 * @param editDataTopicsRequest
 * @returns Updated array of Survey objects
 * @throws Error if the API call fails or returns a non-OK status
 */
export async function editSurveyDataTopics(
  idToken: string,
  editDataTopicsRequest: EditDataTopicsRequest
): Promise<Array<Survey>> {
  const apiUrl = await loadApiUrl()
  const response = await fetch(`${apiUrl}/topics-user-edits`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(editDataTopicsRequest),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const surveys: Array<Survey> = await response.json()
  return surveys
}


/**
 * Add a note to a survey via POST /api/surveys/notes endpoint.
 * The request is routed through CloudFront to the API Gateway.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param addNoteRequest - The note payload containing the surveyId and note text.
 * @returns Updated array of Survey objects reflecting the new note.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function addSurveyNote(
  idToken: string,
  addNoteRequest: AddSurveyNoteRequest
): Promise<Array<Survey>> {
  const apiUrl = await loadApiUrl()
  const response = await fetch(`${apiUrl}/notes`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(addNoteRequest),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const surveys: Array<Survey> = await response.json()
  return surveys
}

/**
 * Add a report to an existing survey via PUT /api/surveys/add-report.
 *
 * Links a biblio source (and optionally its Google Drive file) to the survey
 * by appending the sourceId to the survey's biblioIds and setting the
 * reportBiblioId and reportBiblioFile fields. Triggers regeneration of
 * surveys.json and returns the updated surveys list.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param addReportRequest - The request payload with survey id, sourceId, and optional sourceFileId.
 * @returns Updated array of Survey objects reflecting the linked report.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function addReportToSurvey(
  idToken: string,
  addReportRequest: AddReportToSurveyRequest
): Promise<Array<Survey>> {
  console.log(addReportRequest)
  const apiUrl = await loadApiUrl()
  const response = await fetch(`${apiUrl}/add-report`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(addReportRequest),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const surveys: Array<Survey> = await response.json()
  return surveys
}

export interface AddReportFileToSurveyRequest {
  /** The MAP survey identifier (e.g. "KE2024DHS"). */
  mapSurveyId: string
  /** The Google Drive file ID to set as reportBiblioFile. */
  sourceFileId: string
}

/**
 * Update the reportBiblioFile field on an existing survey via
 * PUT /api/surveys/add-report-file.
 *
 * Sets the reportBiblioFile field to the provided sourceFileId. Triggers
 * regeneration of surveys.json and returns the updated surveys list.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param request - The request payload with survey id and sourceFileId.
 * @returns Updated array of Survey objects reflecting the updated file reference.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function addReportFileToSurvey(
  idToken: string,
  request: AddReportFileToSurveyRequest
): Promise<Array<Survey>> {
  const apiUrl = await loadApiUrl()
  const response = await fetch(`${apiUrl}/add-report-file`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const surveys: Array<Survey> = await response.json()
  return surveys
}
